import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Compass, FileUp, Sparkles, BellRing, ShieldCheck, Lock, Eye, RefreshCw, Check, Car, Plane, ShoppingBag, PawPrint } from "lucide-react";
import i18n, { SUPPORTED_LANGUAGES } from "../i18n";

function LanguageSwitcher() {
  const { i18n: inst } = useTranslation();
  return (
    <Select value={inst.language} onValueChange={(v) => i18n.changeLanguage(v)}>
      <SelectTrigger data-testid="landing-language" className="w-[110px] h-9 rounded-full text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LANGUAGES.map((l) => (
          <SelectItem key={l.code} value={l.code} data-testid={`landing-lang-${l.code}`}>
            <span className="mr-1.5">{l.flag}</span>{l.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Landing() {
  const { t } = useTranslation();

  const steps = [
    { icon: FileUp, title: t("landing.steps.s1Title"), desc: t("landing.steps.s1Desc") },
    { icon: Sparkles, title: t("landing.steps.s2Title"), desc: t("landing.steps.s2Desc") },
    { icon: BellRing, title: t("landing.steps.s3Title"), desc: t("landing.steps.s3Desc") },
  ];
  const examples = [
    { icon: Car, text: t("landing.examples.e1") },
    { icon: ShieldCheck, text: t("landing.examples.e2") },
    { icon: Plane, text: t("landing.examples.e3") },
    { icon: PawPrint, text: t("landing.examples.e4") },
    { icon: ShoppingBag, text: t("landing.examples.e5") },
    { icon: RefreshCw, text: t("landing.examples.e6") },
  ];
  const why = [
    { title: t("landing.why.w1Title"), desc: t("landing.why.w1Desc") },
    { title: t("landing.why.w2Title"), desc: t("landing.why.w2Desc") },
    { title: t("landing.why.w3Title"), desc: t("landing.why.w3Desc") },
    { title: t("landing.why.w4Title"), desc: t("landing.why.w4Desc") },
  ];
  const privacy = [
    { icon: Lock, text: t("landing.privacy.p1") },
    { icon: Eye, text: t("landing.privacy.p2") },
    { icon: ShieldCheck, text: t("landing.privacy.p3") },
    { icon: RefreshCw, text: t("landing.privacy.p4") },
  ];
  const faqs = [
    { q: t("landing.faq.q1"), a: t("landing.faq.a1") },
    { q: t("landing.faq.q2"), a: t("landing.faq.a2") },
    { q: t("landing.faq.q3"), a: t("landing.faq.a3") },
    { q: t("landing.faq.q4"), a: t("landing.faq.a4") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg brand-bg flex items-center justify-center">
              <Compass className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">{t("common.appName")}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login"><Button data-testid="nav-login-btn" variant="ghost">{t("common.login")}</Button></Link>
            <Link to="/register"><Button data-testid="nav-signup-btn" className="rounded-full brand-bg text-white hover:opacity-90">{t("common.startForFree")}</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-brand bg-accent px-3 py-1 rounded-full">
            <Sparkles className="h-3 w-3" /> {t("landing.badge")}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] font-display whitespace-pre-line">
            {t("landing.heroTitle")}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground max-w-md">
            {t("landing.heroSubtitle")}
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link to="/register"><Button data-testid="hero-cta-btn" size="lg" className="rounded-full brand-bg text-white hover:opacity-90 text-base px-7">{t("common.startForFree")}</Button></Link>
            <a href="#how"><Button variant="outline" size="lg" className="rounded-full">{t("common.howItWorks")}</Button></a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> {t("landing.privateFree")}</p>
        </div>
        <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="rounded-2xl overflow-hidden border border-border shadow-xl">
            <img src="https://images.unsplash.com/photo-1449247709967-d4461a6a6103?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt={t("landing.heroImageAlt")} className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section id="how" className="bg-card border-y border-border py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-center">{t("landing.howTitle")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 rounded-2xl bg-accent mx-auto flex items-center justify-center">
                  <s.icon className="h-7 w-7 brand-text" />
                </div>
                <p className="mt-1 text-xs font-mono text-muted-foreground">{t("landing.step", { n: i + 1 })}</p>
                <h3 className="mt-1 text-lg font-semibold font-display">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display">{t("landing.examplesTitle")}</h2>
        <p className="mt-3 text-muted-foreground">{t("landing.examplesSubtitle")}</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {examples.map((e, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
              <e.icon className="h-5 w-5 brand-text shrink-0" />
              <span className="text-sm">{e.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card border-y border-border py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display">{t("landing.whyTitle")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {why.map((w, i) => (
              <div key={i} className="flex gap-4">
                <div className="h-9 w-9 rounded-lg brand-bg flex items-center justify-center shrink-0">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold font-display">{w.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-2xl bg-accent/50 border border-border p-8 sm:p-12">
          <div className="flex items-center gap-2 text-brand">
            <ShieldCheck className="h-6 w-6" />
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-display">{t("landing.privacyTitle")}</h2>
          </div>
          <p className="mt-3 text-muted-foreground max-w-xl">{t("landing.privacySubtitle")}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {privacy.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <p.icon className="h-5 w-5 brand-text shrink-0" />
                <span className="text-sm">{p.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-card border-y border-border py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display">{t("landing.pricingTitle")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-6 text-left">
            <div className="rounded-2xl border border-border p-8 bg-background">
              <p className="font-mono text-xs uppercase text-muted-foreground">{t("landing.free")}</p>
              <p className="mt-2 text-3xl font-bold font-display">$0</p>
              <p className="text-sm text-muted-foreground mt-1">{t("landing.freeLimit")}</p>
              <Link to="/register"><Button className="mt-6 w-full rounded-full" variant="outline">{t("common.startForFree")}</Button></Link>
            </div>
            <div className="rounded-2xl border-2 border-brand p-8 bg-background relative">
              <span className="absolute -top-3 left-8 text-xs font-mono bg-brand text-white px-2 py-0.5 rounded-full">{t("landing.popular")}</span>
              <p className="font-mono text-xs uppercase text-muted-foreground">{t("landing.pro")}</p>
              <p className="mt-2 text-3xl font-bold font-display">$8<span className="text-base font-normal text-muted-foreground">{t("landing.perMonth")}</span></p>
              <p className="text-sm text-muted-foreground mt-1">{t("landing.proLimit")}</p>
              <Link to="/register"><Button className="mt-6 w-full rounded-full brand-bg text-white hover:opacity-90">{t("landing.getPro")}</Button></Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">{t("landing.pricingNote")}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-center">{t("landing.faqTitle")}</h2>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight font-display">{t("landing.finalTitle")}</h2>
          <p className="mt-4 text-primary-foreground/70">{t("landing.finalSubtitle")}</p>
          <Link to="/register"><Button data-testid="final-cta-btn" size="lg" className="mt-8 rounded-full bg-background text-foreground hover:opacity-90 text-base px-8">{t("common.startForFree")}</Button></Link>
        </div>
      </section>

      <footer className="py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Compass className="h-4 w-4 brand-text" /> <span className="font-display font-semibold">{t("common.appName")}</span>
        </div>
        © {new Date().getFullYear()} {t("common.appName")}. {t("landing.footerTagline")}
      </footer>
    </div>
  );
}
